import { NgModule, CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { RouterModule } from "@angular/router";
import { provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { BrowserModule } from "@angular/platform-browser";
import { NoopAnimationsModule } from "@angular/platform-browser/animations";
import { ToastNoAnimationModule } from "ngx-toastr";
import { CKEditorModule } from "@ckeditor/ckeditor5-angular";
import { TooltipModule } from "ngx-bootstrap/tooltip";
// import { BsDropdownModule } from "ngx-bootstrap/dropdown";
// import { ModalModule } from "ngx-bootstrap/modal";
import { JwtModule } from "@auth0/angular-jwt";
import { StoreModule, ActionReducer } from "@ngrx/store";
import { localStorageSync } from "ngrx-store-localstorage";
// import { DragDropModule } from "@angular/cdk/drag-drop";
import { reducers } from "./iappstore";

// Enhanced CKEditor directive
import { CkEditorEnhancedDirective } from "./directives/ck-editor-enhanced.directive";

// Shared components
import { NgSelectComponent } from "./components/shared/ng-select/ng-select.component";

import { AppComponent } from "./components/app/app.component";
import { NavMenuComponent } from "./components/navmenu/navmenu.component";
import { CountryDataTimelineComponent } from "./components/countrydatatimeline/countrydatatimeline.component";
import { HomeComponent } from "./components/home/home.component";
import { EventsComponent } from "./components/events/events.component";
import { EventComponent } from "./components/event/event.component";
import { ConfirmationModalComponent } from "./components/confirmationmodal/confirmationmodal.component";
import { CountrySelectionComponent } from "./components/countryselection/countryselection.component";
import { HtmlContentModalComponent } from "./components/htmlcontentmodal/htmlcontentmodal.component";
import { CountryComponent } from "./components/country/country.component";
import { DropDownsComponent } from "./components/dropdowns/dropdowns.component";
import { DropDownsOverviewComponent } from "./components/dropdownsoverview/dropdownsoverview.component";
import { DropDownComponent } from "./components/dropdown/dropdown.component";
import { DropDownItemComponent } from "./components/dropdownitem/dropdownitem.component";
import { DropDownPrepopulateItemsComponent } from "./components/dropdownprepopulateitems/dropdownprepopulateitems.component";

import { DashboardKeysComponent } from "./components/dashboardkeys/dashboardkeys.component";
import { LatestUpdatesComponent } from "./components/latestupdates/latestupdates.component";
import { LatestUpdateComponent } from "./components/latestupdate/latestupdate.component";
import { HeaderComponent } from "./components/header/header.component";
import { CategoriesComponent } from "./components/itemconfig/categories/categories.component";
import { ItemConfigComponent } from "./components/itemconfig/itemconfig/itemconfig.component";
import { ItemConfigListComponent } from "./components/itemconfig/itemconfiglist/itemconfiglist.component";
import { ItemConfigListItemComponent } from "./components/itemconfig/itemconfiglistitem/itemconfiglistitem.component";
import { ItemConfigManagementComponent } from "./components/itemconfig/itemconfigmanagement/itemconfigmanagement.component";
import { ItemConfigManagementItemComponent } from "./components/itemconfig/itemconfigmanagementitem/itemconfigmanagementitem.component";
import { ManageMetadataComponent } from "./components/metadata/managemetadata/managemetadata.component";
import { MetadataConfigComponent } from "./components/metadata/metadataconfig/metadataconfig.component";
import { KeyContactsComponent } from "./components/keycontacts/keycontacts/keycontacts.component";
import { KeyContactComponent } from "./components/keycontacts/keycontact/keycontact.component";
import { BadRequestComponent } from "./components/badrequest/badrequest.component";
import { LatestUpdateModalComponent } from "./components/latestupdatemodal/latestupdatemodal.component";
import { CommentsModalComponent } from "./components/commentsmodal/commentsmodal.component";
import { LoadingIndicatorComponent } from "./components/loadingindicator/loadingindicator.component";
import { BusinessFunctionSelectionComponent } from "./components/businessfunctionselection/businessfunctionselection.component";
import { BusinessFunctionComponent } from "./components/businessfunction/businessfunction.component";
import { LoginComponent } from "./components/login/login.component";
import { NotFoundComponent } from "./components/notfound/notfound.component";
import { CompanyComponent } from "./components/company/company.component";
import { UsersComponent } from "./components/users/users.component";
import { UserComponent } from "./components/user/user.component";
import { DeloitteAdComponent } from "./components/deloittead/deloittead.component";
import { UnauthorizedComponent } from "./components/unauthorized/unauthorized.component";
import { SecurityGroupComponent } from "./components/securitygroups/securitygroup/securitygroup.component";
import { SecurityGroupsComponent } from "./components/securitygroups/securitygroups/securitygroups.component";
import { ServerErrorComponent } from "./components/servererror/servererror.component";
import { SignedOutComponent } from "./components/signedout/signedout.component";
import { MapContentComponent } from "./components/mapcontent/mapcontent.component";
import { UserSearchModalComponent } from "./components/usersearchmodal/usersearchmodal.component";
import { WorkflowConfigurationComponent } from "./components/workflow/workflowconfiguration/workflowconfiguration.component";
import { WorkflowCountriesComponent } from "./components/workflow/workflowcountries/workflowcountries.component";
import { WorkflowStageDataStatusComponent } from "./components/workflow/workflowstagedatastatus/workflowstagedatastatus.component";
import { DropdownHierarchyComponent } from "./components/dropdownhierarchyconfig/dropdownhierarchy/dropdownhierarchy.component";
import { DropdownHierarchyItemComponent } from "./components/dropdownhierarchyconfig/dropdownhierarchyitem/dropdownhierarchyitem.component";
import { DownloadFileComponent } from "./components/downloadfile/downloadfile.component";
import { ImportFileComponent } from "./components/downloadfile/importFile.component";
import { UsersImportFileComponent } from "./components/usersbulkupload/usersimportFile.component";
import { MetaDataItemSelector } from "./components/metadata/metadataitemselector.component";

import { BusinessFunctionService } from "./services/businessfunction.service";
import { CountryService } from "./services/country.service";
import { EventService } from "./services/event.service";
import { DashboardService } from "./services/dashboard.service";
import { DropDownsService } from "./services/dropdowns.service";
import { DropDownItemService } from "./services/dropdownitem.service";
import { FeatureService } from "./services/feature.service";
import { PresentationTypeService } from "./services/presentationtype.service";
import { ColourPaletteService } from "./services/colourpalette.service";
import { CountryDataTimelineEventService } from "./services/countrydatatimelineevent.service";
import { CountryDashboardService } from "./services/countrydashboard.service";
import { EmitterService } from "./services/emitter.service";
import { KeyContactService } from "./services/keycontact.service";
import { ItemConfigService } from "./services/itemconfig.service";
import { ItemConfigHierachyService } from "./services/itemconfighierachy.service";
import { MetadataService } from "./services/metadata.service";
import { CountryDataService } from "./services/countrydata.service";
import { LatestUpdateService } from "./services/latestupdate.service";
import { CompanyService } from "./services/company.service";
import { AuthGuardService } from "./services/authguard.service";
import { BusinessFunctionGuardService } from "./services/businessfunctionguard.service";
import { UserService } from "./services/user.service";
import { AppUserService } from "./services/appuser.service";
import { ConfigurationService } from "./services/configuration.service";
import { UserCountryTaskService } from "./services/usercountrytask.service";
import { WorkflowConfigurationService } from "./services/workflowconfiguration.service";
import { RoleGuardService } from "./services/roleguard.service";
import { SecurityGroupService } from "./services/securitygroup.service";
import { DropdownHierarchyService } from "./services/dropdownhierarchy.service";
import { DataExportService } from "./services/dataexport.service";
import { DataImportService } from "./services/dataimport.service";
import { UserDataImportService } from "./services/userdataimport.service";
import { PendingChangesGuardService } from "./services/pendingchangesguard.service";

import { BusinessFunctionsResolver } from "./resolvers/businessfunctions.resolver";
import { BusinessFunctionResolver } from "./resolvers/businessfunction.resolver";
import { ColourPalettesResolver } from "./resolvers/colourpalettes.resolver";
import { CompaniesResolver } from "./resolvers/companies.resolver";
import { CountriesResolver } from "./resolvers/countries.resolver";
import { CountryContentStatusResolver } from "./resolvers/countrycontentstatus.resolver";
import { CountryDataTimelineResolver } from "./resolvers/countryDataTimeline.resolver";
import { CountryResolver } from "./resolvers/country.resolver";
import { DefaultWorkflowConfigurationResolver } from "./resolvers/defaultworkflowconfiguration.resolver";
import { DropDownsResolver } from "./resolvers/dropdowns.resolver";
import { DropDownResolver } from "./resolvers/dropdown.resolver";
import { DropDownItemResolver } from "./resolvers/dropdownitem.resolver";
import { FeaturesResolver } from "./resolvers/features.resolver";
import { EventResolver } from "./resolvers/event.resolver";
import { EventsResolver } from "./resolvers/events.resolver";
import { ItemConfigSetResolver, ItemConfigSetByCountryAndIdResolver, ItemConfigResolver } from "./resolvers/itemconfig.resolver";
import { MetadatasResolver } from "./resolvers/metadatas.resolver";
import { MetadataResolver } from "./resolvers/metadata.resolver";
import { MetadataByCountryResolver } from "./resolvers/metadatabycountry.resolver";
import { KeyContactResolver } from "./resolvers/keycontact.resolver";
import { LastUpdateDateResolver } from "./resolvers/lastupdatedate.resolver";
import { LatestUpdatesResolver } from "./resolvers/latestupdates.resolver";
import { LatestUpdateResolver } from "./resolvers/latestupdate.resolver";
import { PresentationTypeResolver } from "./resolvers/presentationType.resolver";
import { RolesResolver } from "./resolvers/roles.resolver";
import { SecurityGroupResolver } from "./resolvers/securitygroup.resolver";
import { SecurityGroupsResolver } from "./resolvers/securitygroups.resolver";
import { UserCountryTasksResolver } from "./resolvers/usercountrytasks.resolver";
import { UserResolver } from "./resolvers/user.resolver";
import { WorkflowConfigurationResolver } from "./resolvers/workflowconfiguration.resolver";
import { WorkflowCountriesResolver } from "./resolvers/workflowcountries.resolver";
import { DropDownHierarchyResolver } from "./resolvers/dropdownhierarchy.resolver";

import { ArrayPropertyFilterPipe } from "./pipes/arrayproperty.pipe";
import { CountdownPipe } from "./pipes/countdown.pipe";
import { CountriesFilterPipe } from "./pipes/countriesfilter.pipe";
import { DisableItemsFilterPipe } from "./pipes/disableitemsfilter.pipe";
import { FromNowPipe } from "./pipes/fromnow.pipe";
import { TruncatePipe } from "./pipes/truncate.pipe";
import { UsersFilterPipe } from "./pipes/usersfilter.pipe";

import { Roles } from "./enums/roles";

import { FeatureToggleDirective } from "./directives/featuretoggle.directive";
import { UserRoleToggleDirective } from "./directives/userroletoggle.directive";
import { ConfirmationCountryModalComponent } from "./components/confirmationcountrydialogmodal/confirmationcountrydialogmodal.component";
import { ConfirmationCountryContentModalComponent } from "./components/confirmationcountrycontentdialogmodal/confirmationcountrycontentdialogmodal.component";

export function localStorageSyncReducer(reducer: ActionReducer<any>): ActionReducer<any> {
  return localStorageSync({ 
    keys: ["azureAuthentication", "user"], 
    rehydrate: true, 
    removeOnUndefined: true,
    storageKeySerializer: (key: string) => `ngrx_${key}`,
    checkStorageAvailability: true
  })(reducer);
}

@NgModule({
    bootstrap: [AppComponent],
    declarations: [
        // All standalone components are in imports
    ],
  providers: [
    AppUserService,
    AuthGuardService,
    BusinessFunctionGuardService,
    BusinessFunctionResolver,
    BusinessFunctionService,
    BusinessFunctionsResolver,
    ColourPaletteService,
    ColourPalettesResolver,
    CompaniesResolver,
    CompanyService,
    ConfigurationService,
    CountryDataTimelineEventService,
    CountryDataTimelineResolver,
    CountriesResolver,
    CountryResolver,
    CountryContentStatusResolver,
    CountryDataService,
    CountryDashboardService,
    CountryService,
    DataImportService,
    UserDataImportService,
    DashboardService,
    DropdownHierarchyService,
    DefaultWorkflowConfigurationResolver,
    DisableItemsFilterPipe,
    DropDownItemResolver,
    DropDownItemService,
    DropDownResolver,
    DropDownsResolver,
    DropDownsService,
    PresentationTypeService,
    EmitterService,
    FeaturesResolver,
    FeatureService,
    WorkflowConfigurationService,
    EventResolver,
    EventService,
    EventsResolver,
    ItemConfigService,
    ItemConfigHierachyService,
    MetadataService,
    DataExportService,
    ItemConfigSetResolver,
    ItemConfigSetByCountryAndIdResolver,
    ItemConfigResolver,
    MetadataByCountryResolver,
    MetadataResolver,
    MetadatasResolver,
    KeyContactResolver,
    KeyContactService,
    LastUpdateDateResolver,
    LatestUpdateResolver,
    LatestUpdateService,
    LatestUpdatesResolver,
    PendingChangesGuardService,
    PresentationTypeResolver,
    RolesResolver,
    RoleGuardService,
    SecurityGroupResolver,
    SecurityGroupsResolver,
    SecurityGroupService,
    UserCountryTasksResolver,
    UserCountryTaskService,
    UserResolver,
    UserService,
    WorkflowConfigurationResolver,
    WorkflowCountriesResolver,
    DropDownHierarchyResolver,
    provideHttpClient(withInterceptorsFromDi())
  ],
  imports: [
    // Angular modules
    CommonModule,
    BrowserModule,
    NoopAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    // DragDropModule,
    
    // Third-party modules
    StoreModule.forRoot((reducers) as any, { metaReducers: [localStorageSyncReducer] }),
    TooltipModule,
    ToastNoAnimationModule.forRoot({
      positionClass: 'toast-top-right',
      preventDuplicates: true,
      timeOut: 5000,
      closeButton: true,
      progressBar: true,
      toastClass: 'ngx-toastr',
      tapToDismiss: true
    }),
    CKEditorModule,
    // BsDropdownModule.forRoot(),
    // ModalModule.forRoot(),
    
    // Standalone components, pipes, and directives
    AppComponent,
    ArrayPropertyFilterPipe,
    BadRequestComponent,
    BusinessFunctionComponent,
    BusinessFunctionSelectionComponent,
    CompanyComponent,
    ConfirmationCountryModalComponent,
    ConfirmationCountryContentModalComponent,
    CountryDataTimelineComponent,
    CountdownPipe,
    CountrySelectionComponent,
    CountryComponent,
    CountriesFilterPipe,
    DashboardKeysComponent,
    DeloitteAdComponent,
    DropDownComponent,
    DropDownItemComponent,
    DropDownsComponent,
    DropDownsOverviewComponent,
    DropDownPrepopulateItemsComponent,
    EventComponent,
    EventsComponent,
    FromNowPipe,
    HeaderComponent,
    HomeComponent,
    CategoriesComponent,
    ItemConfigComponent,
    ItemConfigListComponent,
    ItemConfigListItemComponent,
    ItemConfigManagementComponent,
    ItemConfigManagementItemComponent,
    ManageMetadataComponent,
    MetadataConfigComponent,
    KeyContactsComponent,
    KeyContactComponent,
    LatestUpdateComponent,
    LatestUpdateModalComponent,
    CommentsModalComponent,
    LatestUpdatesComponent,
    LoadingIndicatorComponent,
    ConfirmationModalComponent,
    WorkflowConfigurationComponent,
    WorkflowStageDataStatusComponent,
    LoginComponent,
    MapContentComponent,
    NavMenuComponent,
    NotFoundComponent,
    SecurityGroupComponent,
    SecurityGroupsComponent,
    ServerErrorComponent,
    SignedOutComponent,
    TruncatePipe,
    UnauthorizedComponent,
    UserComponent,
    UsersComponent,
    UsersFilterPipe,
    WorkflowCountriesComponent,
    UserSearchModalComponent,
    HtmlContentModalComponent,
    DropdownHierarchyComponent,
    DropdownHierarchyItemComponent,
    DownloadFileComponent,
    FeatureToggleDirective,
    UserRoleToggleDirective,
    ImportFileComponent,
    UsersImportFileComponent,
    MetaDataItemSelector,
    CkEditorEnhancedDirective,
    NgSelectComponent,
    RouterModule.forRoot([
      {
        path: "",
        redirectTo: "login",
        pathMatch: "full"
      }, {
        path: "home",
        component: HomeComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          lastValidatedDate: LastUpdateDateResolver,
          userCountryTasks: UserCountryTasksResolver
        },
        data: {
          title: "Home",
          navigation: {
            tooltip: "Home",
            icon: "fa-home",
            sortOrder: 1
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "countries",
        component: CountrySelectionComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          countries: CountryContentStatusResolver
        },
        data: {
          navigation: {
            tooltip: "Countries",
            icon: "fa-globe",
            sortOrder: 3
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "countries/:countryCode",
        component: CountryComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          country: CountryResolver
        },
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "countries/:countryCode/:id",
        component: ItemConfigManagementComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          configset: ItemConfigSetByCountryAndIdResolver,
          timeline: CountryDataTimelineResolver,
          itemmetadata: MetadataByCountryResolver,
          dropdowns: DropDownsResolver
        },
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "map",
        component: MapContentComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver
        },
        data: {
          navigation: {
            tooltip: "Map content",
            icon: "fa-map",
            sortOrder: 4
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "categories",
        component: CategoriesComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        data: {
          navigation: {
            tooltip: "Categories",
            icon: "fa-align-left",
            sortOrder: 2
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        },
        children: [
          {
            path: "",
            redirectTo: "",
            pathMatch: "full"
          },
          {
            path: "itemconfig",
            component: ItemConfigComponent,
            canActivate: [
              AuthGuardService,
              BusinessFunctionGuardService,
              RoleGuardService
            ],
            resolve: {
              configset: ItemConfigSetResolver,
              dropdowns: DropDownsResolver
            },
            data: {
              title: "Content structure",
              security: {
                roles: [
                  Roles[Roles.SuperAdmin],
                  Roles[Roles.Admin]
                ]
              }
            }
          },
          {
            path: "managemetadata",
            component: ManageMetadataComponent,
            canActivate: [
              AuthGuardService,
              BusinessFunctionGuardService,
              RoleGuardService
            ],
            resolve: {
              metadataList: MetadatasResolver
            },
            data: {
              title: "Manage metadata",
              security: {
                roles: [
                  Roles[Roles.SuperAdmin],
                  Roles[Roles.Admin],
                  Roles[Roles.ContentEditor]
                ]
              }
            }
          }
        ]
      }, {
        path: "timeline",
        component: EventsComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          events: EventsResolver
        },
        data: {
          navigation: {
            tooltip: "Timeline",
            icon: "fa-circle-o",
            sortOrder: 5
          },
          title: "Timeline",
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "event",
        component: EventComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver
        },
        data: {
          title: "Edit event",
          breadcrumbs: [
            { path: "timeline" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "event/:id",
        component: EventComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver,
          event: EventResolver
        },
        data: {
          title: "Edit event",
          breadcrumbs: [
            { path: "timeline" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "dropdowns",
        component: DropDownsComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        data: {
          title: "Drop down configuration",
          navigation: {
            tooltip: "Drop down configuration",
            icon: "fa-list",
            sortOrder: 7
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        },
        children: [
          {
            path: "",
            redirectTo: "overview",
            pathMatch: "full"
          },
          {
            path: "overview",
            component: DropDownsOverviewComponent,
            canActivate: [
              AuthGuardService,
              BusinessFunctionGuardService,
              RoleGuardService
            ],
            resolve: {
              dropDowns: DropDownsResolver
            },
            data: {
              title: "Overview",
              navigation: {
                tooltip: "Drop down configuration",
                icon: "fa-list",
                sortOrder: 7
              },
              security: {
                roles: [
                  Roles[Roles.SuperAdmin],
                  Roles[Roles.Admin]
                ]
              }
            }
          },
          {
            path: "manage",
            component: DropdownHierarchyComponent,
            canActivate: [
              AuthGuardService,
              BusinessFunctionGuardService,
              RoleGuardService
            ],
            resolve: {
              dropdownhierarchy: DropDownHierarchyResolver,
              availabledropdowns: DropDownsResolver
            },
            data: {
              title: "Manage hierarchy",
              security: {
                roles: [
                      Roles[Roles.SuperAdmin],
                      Roles[Roles.Admin]
                  ]
              }
            }
          }
        ]
      }, {
        path: "dropdown/:id",
        component: DropDownComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          dropDown: DropDownResolver,
          presentationType: PresentationTypeResolver,
        },
        data: {
          title: "Edit drop down",
          breadcrumbs: [
            { path: "dropdowns" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "dropdown",
        component: DropDownComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          presentationType: PresentationTypeResolver,
        },
        data: {
          title: "Create drop down",
          breadcrumbs: [
            { path: "dropdowns" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "dropdownitem/:dropDownId/:id",
        component: DropDownItemComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          dropDown: DropDownResolver,
          dropDownItem: DropDownItemResolver,
          colourPalettes: ColourPalettesResolver
        },
        data: {
          title: "Edit drop down item",
          breadcrumbs: [
            { path: "dropdowns" },
            { path: "dropdown" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "dropdownitem/:dropDownId",
        component: DropDownItemComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          dropDown: DropDownResolver,
          colourPalettes: ColourPalettesResolver
        },
        data: {
          title: "Create drop down item",
          breadcrumbs: [
            { path: "dropdowns" },
            { path: "dropdown" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "dropdownprepopulateitems/:dropDownId",
        component: DropDownPrepopulateItemsComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          dropDown: DropDownResolver
        },
        data: {
          title: "Prepopulate drop down items",
          breadcrumbs: [
            { path: "dropdowns" },
            { path: "dropdown" }
          ],
          security: {
            roles: [
                  Roles[Roles.SuperAdmin],
                  Roles[Roles.Admin]
            ]
          }
        }
      },
      {
        path: "latestupdates",
        component: LatestUpdatesComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          latestupdates: LatestUpdatesResolver
        },
        data: {
          title: "Latest updates",
          navigation: {
            tooltip: "Latest updates",
            icon: "fa-calendar-o",
            sortOrder: 8
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "latestupdate",
        component: LatestUpdateComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          latestupdate: LatestUpdateResolver,
          countries: CountriesResolver
        },
        data: {
          title: "Create latest update",
          breadcrumbs: [
            { path: "latestupdates" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "latestupdate/:id",
        component: LatestUpdateComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          latestupdate: LatestUpdateResolver
        },
        data: {
          title: "Edit latest update",
          breadcrumbs: [
            { path: "latestupdates" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "company",
        component: CompanyComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        data: {
          navigation: {
            tooltip: "Companies",
            icon: "fa-building",
            sortOrder: 9
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "workflow/:countryname/:countrycode",
        component: WorkflowConfigurationComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          workflowconfiguration: WorkflowConfigurationResolver,
          defaultWorkflowConfiguration: DefaultWorkflowConfigurationResolver
        },
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "workflow",
        component: WorkflowCountriesComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          workflowCountries: WorkflowCountriesResolver
        },
        data: {
          navigation: {
            tooltip: "Workflow",
            icon: "fa-cogs",
            sortOrder: 5
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "businessfunctionselection",
        component: BusinessFunctionSelectionComponent,
        canActivate: [AuthGuardService],
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "users",
        component: UsersComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        data: {
          title: "Manage users",
          navigation: {
            tooltip: "Users",
            icon: "fa-user",
            sortOrder: 11
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "user/:id",
        component: UserComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver,
          user: UserResolver,
          roles: RolesResolver,
          companies: CompaniesResolver
        },
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          },
          title: "Edit user",
          breadcrumbs: [
            { path: "users" }
          ]
        }
      }, {
        path: "user",
        component: UserComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver,
          roles: RolesResolver,
          companies: CompaniesResolver
        },
        data: {
          title: "Create user",
          breadcrumbs: [
            { path: "users" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "securitygroups/list",
        component: SecurityGroupsComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          securityGroups: SecurityGroupsResolver
        },
        data: {
          title: "Security groups",
          navigation: {
            tooltip: "Security groups",
            icon: "fa-group",
            sortOrder: 12
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "securitygroups/securitygroup/:id",
        component: SecurityGroupComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          securityGroup: SecurityGroupResolver
        },
        data: {
          title: "Edit security group",
          breadcrumbs: [
            { path: "securitygroups/list" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "securitygroups/securitygroup",
        component: SecurityGroupComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        data: {
          title: "Create security group",
          breadcrumbs: [
            { path: "securitygroups/list" }
          ],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "businessfunction",
        component: BusinessFunctionComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          businessFunction: BusinessFunctionResolver,
          features: FeaturesResolver
        },
        data: {
          navigation: {
            tooltip: "Configuration",
            icon: "fa-cog",
            sortOrder: 10
          },
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "login",
        component: LoginComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "deloittead",
        component: DeloitteAdComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "signedout",
        component: SignedOutComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "unauthorized",
        component: UnauthorizedComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "notfound",
        component: NotFoundComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "servererror",
        component: ServerErrorComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "badrequest",
        component: BadRequestComponent,
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "keycontacts/keycontact",
        component: KeyContactComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      }, {
        path: "keycontacts/keycontact/:id",
        component: KeyContactComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        resolve: {
          keyContact: KeyContactResolver
        },
        data: {
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin]
            ]
          }
        }
      },
      {
        path: "metadata",
        component: MetadataConfigComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver
        },
        data: {
          title: "Manage metadata",
          breadcrumbs: [{ path: "metadata" }],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "metadata/:id",
        component: MetadataConfigComponent,
        canActivate: [
          AuthGuardService,
          BusinessFunctionGuardService,
          RoleGuardService
        ],
        canDeactivate: [
          PendingChangesGuardService
        ],
        resolve: {
          countries: CountriesResolver,
          metadata: MetadataResolver
        },
        data: {
          title: "Edit metadata",
          breadcrumbs: [{ path: "metadata" }],
          security: {
            roles: [
              Roles[Roles.SuperAdmin],
              Roles[Roles.Admin],
              Roles[Roles.ContentEditor]
            ]
          }
        }
      }, {
        path: "**",
        redirectTo: "login"
        },
       
    ])
  ],
  schemas: [
    CUSTOM_ELEMENTS_SCHEMA,
    NO_ERRORS_SCHEMA
  ],
  exports: [
    FormsModule,
    ReactiveFormsModule,
    CommonModule,
    // DragDropModule,
    CKEditorModule
  ]
})
export class AppModule {
}
